import { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import type { CreateTourItemViewModel } from './models'
import type { CreateItemProps } from './utils'
import { createTourPayloadFromViewModel } from './utils'
import { formOptions } from '../Common/Fields/config'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TourFields from 'tripPlanner/components/TripModal/TripItems/Common/TourFields'
import { useCreateTripItem } from 'tripPlanner/hooks/api/tripItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import ModalFooter from 'LuxKit/Modal/ModalFooter'

type ViewModel = CreateTourItemViewModel

function CreateTour(props: CreateItemProps) {
  const { tripId, defaultDate } = props
  const intl = useIntl()

  const { closeModal, goBack } = useModalCallbacks()

  const methods = useForm<ViewModel>({
    ...formOptions,
    defaultValues: {
      type: 'TOUR',
      sourceType: 'NA',
      isBooked: false,
      startDate: defaultDate,
      endDate: defaultDate,
      startPlace: {},
      endPlace: {},
    },
  })
  const { handleSubmit } = methods

  const { mutate: createMutate, isLoading } = useCreateTripItem({
    onSuccess: () => {
      closeModal()
    },
  })

  const createTourCruiseItem = useCallback(
    (viewModel: ViewModel) => {
      createMutate({
        tripId,
        tripItem: createTourPayloadFromViewModel(viewModel),
      })
    },
    [createMutate, tripId],
  )

  const modalTitle = intl.formatMessage({
    id: 'wWjka05jD66exZjVDsba',
    defaultMessage: 'Add tour',
    description: 'Modal title when adding a tour to trip on the tour offer detail page',
  })
  const saveLabel = intl.formatMessage({
    id: '06Rhh7VAKVjpxL1AgMVF',
    defaultMessage: 'Save',
    description: 'Save button label in the add tour modal on the tour offer detail page',
  })

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <FormProvider {...methods}>
        <ModalBody>
          <ModalContent>
            <TourFields tripId={tripId} />
          </ModalContent>
        </ModalBody>

        <ModalFooter
          primaryActionProps={{
            onClick: handleSubmit(createTourCruiseItem),
            children: saveLabel,
            disabled: isLoading,
          }}
        />
      </FormProvider>

      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default CreateTour
